import {
  ChevronRight,
  Droplet,
  Package,
  Thermometer,
  TriangleAlert,
  WifiOff,
  Wind,
} from 'lucide-react';
import type { IoTReading, NaveSensores } from '@/lib/queries/iot';
import { Link } from 'react-router-dom';
import { applySensorEquipment, getSensorEquipment } from '@/lib/sensorEquipment';
import { fmt1, fmtFechaHora, fmtInt, fmtM3 } from '@/lib/format';
import {
  hasSensorAlert,
  sensorAlertLabel,
  sensorStationName as stationName,
} from '@/lib/iotPresentation';

function sensorLabel(reading: IoTReading | null | undefined) {
  return reading?.state === 'alert'
    ? sensorAlertLabel(reading)
    : reading?.state === 'disconnected'
      ? `Sin conexión${hasSensorAlert(reading) ? ` · ${sensorAlertLabel(reading)}` : ''}`
      : reading?.state === 'normal'
        ? 'Normal'
        : 'Sin lecturas';
}
function naveAlerts(data: NaveSensores | undefined) {
  return (data?.environment ?? []).flatMap((device, index) =>
    [device.temperature, device.humidity].filter(hasSensorAlert).map((r) => ({
      deviceId: device.device_id,
      station: device.station ?? stationName(index),
      reading: r!,
    })),
  );
}
function sensorValue(reading: IoTReading | null | undefined) {
  if (reading?.value == null) return '—';
  return reading.unit === 'liter'
    ? fmtM3(reading.value)
    : reading.unit === 'celsius'
      ? `${fmt1(reading.value)} °C`
      : reading.unit === 'percent'
        ? `${fmt1(reading.value)} %`
        : `${fmtInt(reading.value)} ${reading.unit === 'kilogram' ? 'kg' : reading.unit}`;
}
export function NaveSensorAlerts({
  data: raw,
  onSelect,
}: {
  data: NaveSensores | undefined;
  onSelect: (device: number | string | null) => void;
}) {
  const data = raw ? applySensorEquipment(raw, getSensorEquipment(undefined, raw)) : undefined;
  const disconnected = (data?.environment ?? []).filter((d) =>
    [d.temperature, d.humidity].some((r) => r?.state === 'disconnected'),
  );
  const fullyDisconnected = disconnected.every((d) =>
    [d.temperature, d.humidity].every(
      (r) => !r || r.state === 'disconnected' || r.state === 'no_data',
    ),
  );
  return (
    <div className="nave-alerts">
      {naveAlerts(data).map(({ deviceId, station, reading }, index) => (
        <button
          key={`${deviceId}-${index}`}
          className="nave-alert"
          onClick={() => onSelect(deviceId ?? station)}
        >
          {reading.state === 'alert' ? (
            <TriangleAlert size={20} aria-hidden />
          ) : (
            <WifiOff size={20} aria-hidden />
          )}
          <span>
            <strong>
              {sensorAlertLabel(reading)} · Estación {station}
            </strong>
            <small>
              {sensorValue(reading)}
              {reading.received_at ? ` · ${fmtFechaHora(reading.received_at)}` : ''}
            </small>
          </span>
          <ChevronRight size={16} aria-hidden />
        </button>
      ))}
      {[data?.ammonia, data?.water, data?.silo].filter(hasSensorAlert).map((reading, index) => (
        <button
          key={`other-${index}`}
          className="nave-alert"
          onClick={() =>
            onSelect(data?.environment[0]?.device_id ?? data?.environment[0]?.station ?? null)
          }
        >
          <TriangleAlert size={20} aria-hidden />
          <span>
            <strong>{sensorAlertLabel(reading)}</strong>
            <small>
              {sensorValue(reading)}
              {reading?.received_at ? ` · ${fmtFechaHora(reading.received_at)}` : ''}
            </small>
          </span>
          <ChevronRight size={16} aria-hidden />
        </button>
      ))}
      {disconnected.length > 0 && (
        <button
          className="nave-alert nave-alert-muted"
          onClick={() => onSelect(disconnected[0].device_id ?? disconnected[0].station ?? null)}
        >
          <WifiOff size={20} aria-hidden />
          <span>
            <strong>
              {fullyDisconnected
                ? `${disconnected.length} ${disconnected.length === 1 ? 'estación sin conexión' : 'estaciones sin conexión'}`
                : 'Lecturas sin actualizar'}
            </strong>
            <small>
              Las lecturas desconectadas se excluyen de las medias. Consultar estaciones.
            </small>
          </span>
          <ChevronRight size={16} aria-hidden />
        </button>
      )}
    </div>
  );
}
export function NaveSensors({
  data: raw,
  loading,
  error,
  selectedDevice,
  onSelect,
  configureTo,
}: {
  data: NaveSensores | undefined;
  loading: boolean;
  error: boolean;
  selectedDevice: number | string | null;
  onSelect: (device: number | string | null) => void;
  configureTo?: string;
}) {
  const equipment = getSensorEquipment(undefined, raw);
  const data = raw ? applySensorEquipment(raw, equipment) : undefined;
  const devices = data?.environment ?? [];
  const selected =
    devices.find(
      (d) =>
        selectedDevice != null && (d.device_id === selectedDevice || d.station === selectedDevice),
    ) ?? devices[0];
  const index = selected ? devices.indexOf(selected) : 0;
  const allReadings = [
    ...devices.flatMap((d) => [d.temperature, d.humidity]),
    data?.ammonia,
    data?.water,
    data?.silo,
  ];
  if (!data)
    return (
      <p role={error ? 'alert' : 'status'}>
        {loading
          ? 'Cargando sensores…'
          : error
            ? 'Sensores no disponibles. Conecta para actualizar.'
            : 'Sin telemetría.'}
      </p>
    );
  if (!devices.length && !equipment.ammonia && !equipment.water && !equipment.silo)
    return (
      <div className="nave-tab-content">
        <p>Sin sensores instalados</p>
        {configureTo && (
          <Link to={configureTo} className="ui-text-button">
            Configurar sensores
          </Link>
        )}
      </div>
    );
  return (
    <div className="nave-tab-content">
      {data.local_snapshot && (
        <p className="nave-caption" role="status">
          Lecturas conservadas · Sin actualización
          {data.cached_at ? ` · ${fmtFechaHora(data.cached_at)}` : ''}
        </p>
      )}
      {error && <p role="alert">No se han podido actualizar las lecturas.</p>}
      {devices.length > 0 ? (
        <>
          <section className="nave-stations" aria-label="Estaciones de la nave">
            <div
              className="nave-roof"
              style={{ gridTemplateColumns: `repeat(${devices.length}, minmax(48px, 1fr))` }}
            >
              {devices.map((device, i) => {
                const readings = [device.temperature, device.humidity];
                const alert = readings.some((r) => r?.state === 'alert');
                const available = readings.some(
                  (r) => r?.state === 'normal' || r?.state === 'alert',
                );
                const station = device.station ?? stationName(i);
                const disconnected = readings.some((r) => r?.state === 'disconnected');
                return (
                  <button
                    key={station}
                    className={`nave-station ${alert ? 'nave-station-alert' : !available ? 'nave-station-off' : ''}`}
                    aria-label={`Estación ${station}${alert ? ', alerta' : !available ? (disconnected ? ', sin lectura actual' : ', sin lecturas') : ''}`}
                    aria-pressed={selected?.station === station}
                    onClick={() => onSelect(device.device_id ?? station)}
                  >
                    {station}
                    {alert && <TriangleAlert size={14} aria-hidden />}
                  </button>
                );
              })}
            </div>
            <p className="nave-caption nave-spread">
              <span>Esquema orientativo</span>
              <span>Selecciona una estación</span>
            </p>
          </section>
          <section
            className="nave-station-detail"
            data-testid="iot-temperatura-card"
            aria-label={`Lecturas de estación ${selected.station ?? stationName(index)}`}
            aria-live="polite"
          >
            <h2>Estación {selected.station ?? stationName(index)}</h2>
            <div className="nave-station-values">
              {[
                {
                  reading: selected?.temperature,
                  Icon: Thermometer,
                  name: 'Temperatura',
                  test: 'iot-temperatura-estado',
                },
                { reading: selected?.humidity, Icon: Droplet, name: 'Humedad' },
              ].map(({ reading, Icon, name, test }) => (
                <div key={name}>
                  <span>
                    <Icon size={17} aria-hidden />
                    {name}
                  </span>
                  <strong className={reading?.state === 'alert' ? 'text-warning-text' : ''}>
                    {sensorValue(reading)}
                  </strong>
                  <small data-testid={test}>
                    {sensorLabel(reading)}
                    {reading?.simulated && !hasSensorAlert(reading) ? ' · Simulada' : ''}
                  </small>
                  {reading?.received_at && (
                    <small>Recibido: {fmtFechaHora(reading.received_at)}</small>
                  )}
                  {reading?.state === 'disconnected' && (
                    <small>Última lectura. Excluida de la media.</small>
                  )}
                </div>
              ))}
            </div>
          </section>
        </>
      ) : null}
      {(equipment.ammonia || equipment.water || equipment.silo) && (
        <section aria-labelledby="nave-other-title">
          <h2 id="nave-other-title">{devices.length ? 'Otros sensores' : 'Lecturas'}</h2>
          {[
            { name: 'Amoníaco', reading: data.ammonia, Icon: Wind },
            { name: 'Agua acumulada', reading: data.water, Icon: Droplet },
            { name: 'Peso del silo', reading: data.silo, Icon: Package },
          ]
            .filter(({ reading }) => reading != null)
            .map(({ name, reading, Icon }) => (
              <div key={name} className="nave-reading">
                <span>
                  <Icon size={19} aria-hidden />
                  {name}
                  <small>
                    {sensorLabel(reading)}
                    {reading?.simulated && !hasSensorAlert(reading) ? ' · Simulada' : ''}
                    {reading?.received_at ? ` · ${fmtFechaHora(reading.received_at)}` : ''}
                  </small>
                </span>
                <strong className={reading?.state === 'alert' ? 'text-warning-text' : ''}>
                  {sensorValue(reading)}
                </strong>
              </div>
            ))}
          {allReadings.every((r) => !r || r.state === 'no_data') && (
            <p className="nave-caption">No hay lecturas registradas.</p>
          )}
        </section>
      )}
    </div>
  );
}
